import './HumanFigure.css';

interface HumanFigureProps {
  activeLayer?: 'sensory' | 'integration' | 'symbolic' | null;
}

export function HumanFigure({ activeLayer }: HumanFigureProps) {
  return (
    <svg
      className="human-figure"
      viewBox="0 0 200 440"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-labelledby="hf-title hf-desc"
    >
      <title id="hf-title">Human figure</title>
      <desc id="hf-desc">
        A gender-neutral humanoid silhouette used as an interactive anchor
        for exploring experiential notation hotspots.
      </desc>

      <defs>
        {/* Body gradient — light panels */}
        <linearGradient id="hf-bodyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2493A2" stopOpacity="0.28" />
          <stop offset="50%" stopColor="#57BDA2" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#2493A2" stopOpacity="0.22" />
        </linearGradient>

        {/* Stroke gradient */}
        <linearGradient id="hf-strokeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#57BDA2" stopOpacity="0.85" />
          <stop offset="50%" stopColor="#2493A2" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#57BDA2" stopOpacity="0.85" />
        </linearGradient>

        {/* Joint gradient — darker segments for articulation points */}
        <linearGradient id="hf-jointGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a6b75" stopOpacity="0.42" />
          <stop offset="100%" stopColor="#134550" stopOpacity="0.48" />
        </linearGradient>

        {/* Subtle inner shadow for depth */}
        <linearGradient id="hf-shading" x1="30%" y1="0%" x2="70%" y2="100%">
          <stop offset="0%" stopColor="#2493A2" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#1a6b75" stopOpacity="0.14" />
        </linearGradient>

        {/* Ambient aura */}
        <radialGradient id="hf-aura" cx="50%" cy="50%" r="52%">
          <stop offset="0%" stopColor="#2493A2" stopOpacity="0.1" />
          <stop offset="60%" stopColor="#57BDA2" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#2493A2" stopOpacity="0" />
        </radialGradient>

        {/* Layer highlight — sensory (head region) */}
        <radialGradient id="hf-sensoryHL" cx="50%" cy="12%" r="20%">
          <stop offset="0%" stopColor="#2493A2" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#2493A2" stopOpacity="0" />
        </radialGradient>

        {/* Layer highlight — integration (torso region) */}
        <radialGradient id="hf-integrationHL" cx="50%" cy="48%" r="30%">
          <stop offset="0%" stopColor="#57BDA2" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#57BDA2" stopOpacity="0" />
        </radialGradient>

        <filter id="hf-softGlow" x="-15%" y="-15%" width="130%" height="130%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer aura */}
      <ellipse cx="100" cy="220" rx="88" ry="200"
        fill="url(#hf-aura)" className="human-figure__aura"
      />

      {/* Layer-specific glow highlights */}
      {activeLayer === 'sensory' && (
        <ellipse cx="100" cy="36" rx="50" ry="55"
          fill="url(#hf-sensoryHL)" className="human-figure__layer-glow"
        />
      )}
      {activeLayer === 'integration' && (
        <ellipse cx="100" cy="150" rx="65" ry="90"
          fill="url(#hf-integrationHL)" className="human-figure__layer-glow"
        />
      )}

      {/*
        Refined 8-head canon, segmented robot aesthetic
        H = 52.5px, viewBox 200×440, usable y: 10–430
        Head +10% (rx=22 ry=29), shoulders ~2.5 head widths (110px span)
        Elbows at natural waist (y≈178), fingertips at mid-thigh (y≈275)
        Torso: shoulder→waist taper→pelvis widening (non-gendered)
        Thighs widened for balance
      */}
      <g filter="url(#hf-softGlow)">

        {/* ── Head ── dark helmet dome (+10%: rx 20→22, ry 26→29) */}
        <ellipse cx="100" cy="36" rx="22" ry="29"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.4"
        />

        {/* ── Neck ── dark joint connector (widened 91–109 to match head) */}
        <path d="M 91 63 L 91 74 L 109 74 L 109 63"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Torso chest plate ── shoulder→waist taper→pelvis hint */}
        <path d="
          M 91 76
          C 78 76, 64 80, 57 84
          Q 51 88, 51 94
          C 54 115, 60 140, 66 158
          C 70 170, 73 178, 73 182
          Q 72 187, 71 190
          L 129 190
          Q 128 187, 127 182
          C 127 178, 130 170, 134 158
          C 140 140, 146 115, 149 94
          Q 149 88, 143 84
          C 136 80, 122 76, 109 76
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.4"
        />

        {/* Torso depth shading — right half */}
        <path d="
          M 109 76
          C 122 76, 136 80, 143 84
          Q 149 88, 149 94
          C 146 115, 140 140, 134 158
          C 130 170, 127 178, 127 182
          Q 128 187, 129 190
          L 100 190 L 100 76 Z"
          fill="url(#hf-shading)"
        />

        {/* ── Hip / Pelvis ── dark joint, widens at pelvis then narrows to crotch */}
        <path d="
          M 70 192
          C 69 198, 67 204, 66 210
          C 66 216, 68 222, 72 228
          Q 86 234, 100 234
          Q 114 234, 128 228
          C 132 222, 134 216, 134 210
          C 133 204, 131 198, 130 192
          Z"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Left shoulder joint ── dark cap */}
        <ellipse cx="53" cy="86" rx="8" ry="7"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Left upper arm ── lengthened, elbow at natural waist y≈178 */}
        <path d="
          M 35 92
          C 33 102, 31 118, 30 136
          L 29 154
          C 28 162, 28 168, 28 172
          L 50 172
          C 50 168, 50 162, 51 154
          L 52 136
          C 52 118, 52 102, 50 92
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Left elbow ── dark joint at natural waist */}
        <ellipse cx="39" cy="178" rx="12" ry="8"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Left forearm ── shifted down to match elbow */}
        <path d="
          M 31 186
          C 31 196, 31 208, 31 218
          L 31 228
          L 49 228
          L 49 218
          C 49 208, 49 196, 49 186
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Left hand ── larger, fingertips at mid-thigh y=275 */}
        <path d="
          M 32 230
          C 30 240, 28 252, 28 262
          C 28 270, 30 274, 35 276
          L 45 276
          C 50 274, 52 270, 52 262
          C 52 252, 50 240, 48 230
          Z"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Right shoulder joint ── dark cap */}
        <ellipse cx="147" cy="86" rx="8" ry="7"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Right upper arm ── lengthened, mirrored */}
        <path d="
          M 150 92
          C 148 102, 148 118, 148 136
          L 149 154
          C 150 162, 150 168, 150 172
          L 172 172
          C 172 168, 172 162, 171 154
          L 170 136
          C 169 118, 167 102, 165 92
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Right elbow ── dark joint at natural waist */}
        <ellipse cx="161" cy="178" rx="12" ry="8"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Right forearm ── shifted down to match elbow */}
        <path d="
          M 151 186
          C 151 196, 151 208, 151 218
          L 151 228
          L 169 228
          L 169 218
          C 169 208, 169 196, 169 186
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Right hand ── larger, fingertips at mid-thigh y=275 */}
        <path d="
          M 152 230
          C 150 240, 148 252, 148 262
          C 148 270, 150 274, 155 276
          L 165 276
          C 170 274, 172 270, 172 262
          C 172 252, 170 240, 168 230
          Z"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Left thigh ── wider (24px) for better balance */}
        <path d="
          M 76 236
          C 74 248, 70 266, 68 284
          C 66 300, 66 312, 66 318
          L 92 318
          C 92 312, 92 300, 90 284
          C 88 266, 86 248, 84 236
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Left knee ── dark joint (widened to match thigh) */}
        <ellipse cx="79" cy="325" rx="14" ry="9"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Left shin ── light panel */}
        <path d="
          M 70 334
          C 70 346, 70 362, 70 378
          C 70 392, 70 402, 70 408
          L 90 408
          C 90 402, 90 392, 90 378
          C 90 362, 90 346, 90 334
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Left foot ── light panel */}
        <path d="
          M 68 410
          C 66 416, 62 420, 56 424
          C 52 426, 50 428, 52 430
          L 90 430
          C 92 428, 92 424, 90 418
          C 90 414, 90 412, 90 410
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Right thigh ── wider (24px) for better balance */}
        <path d="
          M 116 236
          C 114 248, 112 266, 110 284
          C 110 300, 110 312, 110 318
          L 134 318
          C 134 312, 134 300, 132 284
          C 130 266, 126 248, 124 236
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Right knee ── dark joint (widened to match thigh) */}
        <ellipse cx="121" cy="325" rx="14" ry="9"
          fill="url(#hf-jointGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />

        {/* ── Right shin ── light panel */}
        <path d="
          M 110 334
          C 110 346, 110 362, 110 378
          C 110 392, 110 402, 110 408
          L 130 408
          C 130 402, 130 392, 130 378
          C 130 362, 130 346, 130 334
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1.2"
        />

        {/* ── Right foot ── light panel */}
        <path d="
          M 110 410
          C 110 412, 110 414, 110 418
          C 108 424, 108 428, 110 430
          L 148 430
          C 150 428, 148 426, 144 424
          C 138 420, 134 416, 132 410
          Z"
          fill="url(#hf-bodyGrad)" stroke="url(#hf-strokeGrad)" strokeWidth="1"
        />
      </g>

      {/* Inner energy lines — midline + cross bands at chest, waist, pelvis */}
      <g opacity="0.12">
        <line x1="100" y1="66" x2="100" y2="220"
          stroke="#57BDA2" strokeWidth="0.8" strokeDasharray="5 4"
        />
        <path d="M 58 130 Q 100 138 142 130" stroke="#57BDA2" strokeWidth="0.5" fill="none" />
        <path d="M 70 170 Q 100 176 130 170" stroke="#57BDA2" strokeWidth="0.5" fill="none" />
        <path d="M 76 210 Q 100 216 124 210" stroke="#57BDA2" strokeWidth="0.5" fill="none" />
      </g>

      {/* Heart center pulse — mid-chest */}
      <circle cx="100" cy="145" r="5"
        fill="none" stroke="#57BDA2" strokeWidth="0.8" opacity="0.35"
        className="human-figure__heart"
      />
      <circle cx="100" cy="145" r="2"
        fill="#57BDA2" opacity="0.45"
        className="human-figure__heart-core"
      />
    </svg>
  );
}
